import * as React from 'react';
import translate from '../../../../locale/translate';

import InventoryRow from './InventoryRow';
import ISettings from './ISettings';
import {renderChart} from './ChartHelper';

interface IProps {
    inventoryItem: any,
    settings: ISettings,
}

export default class InventoryTable extends React.Component<IProps, {}> {
    static propTypes = {
    };

    renderDataTable(inventoryCategoryData, tableDefinition) {
        const randomId = Math.random().toString(36).substring(7);

        const rows = inventoryCategoryData.map((data, key) => {
            const rowId = `table-${randomId}-${data.key}-${key}`;
            const rowData = tableDefinition.map(col => {
                if (col.cellDataFn && typeof col.cellDataFn === "function") {
                    return col.cellDataFn(data);
                }
                return data[col.key]
            });
            return <InventoryRow key={rowId} id={rowId} rowValues={rowData} />
        });

        return <div>
            <table className="table" key={`table-${randomId}`}>
                <thead>
                    <tr>
                        {tableDefinition.map(col => <th key={`table-${randomId}-${col.key}`}>{col.title}</th>)}
                    </tr>
                </thead>
                <tbody>
                    {rows}
                </tbody>
            </table>
        </div>;
    }

    renderBlocks(category, baseKey, inventoryCategoryData): JSX.Element {
        const mainData = inventoryCategoryData[0];

        if (category === "BIOS" && mainData) {
            return <div>
                <div><span className='emphasized'>{translate.gettext("BIOS")}</span>: {mainData.BIOSVENDOR} {mainData.BIOSVERSION} ({mainData.BIOSDATE})</div>
                <div><span className='emphasized'>{translate.gettext("MAINBOARD")}</span>: {mainData.MAINBOARDMANUFACTURER} {mainData.MAINBOARDMODEL} (S/N {mainData.MAINBOARDSERIAL})</div>
            </div>;
        }

        if (category === "CPU" && mainData) {
            return inventoryCategoryData.map((cpuData, i) => <div>
                <div key={`${baseKey}-CPU-${i}`}>{cpuData.MARKETINGNAME || cpuData.CPUNAME} {cpuData.FREQUENCY} {translate.gettext("MHz")} - {cpuData.CPU_COUNT} {translate.gettext("cores")} (S/N {cpuData.SERIALNUMBER})</div>
            </div>);
        }

        if (category === "MEMORYDEVICE" && inventoryCategoryData && inventoryCategoryData.length) {
            const tableDefinition = [
                {key: "DEVICELOCATOR", title: translate.gettext("DEVICELOCATOR")},
                {key: "DEVICEMANUFACTURER", title: translate.gettext("DEVICEMANUFACTURER")},
                {key: "DEVICEPARTNUMBER", title: translate.gettext("DEVICEPARTNUMBER")},
                {key: "DEVICESIZE", title: `${translate.gettext("DEVICESIZE")} (MB)`},
                {key: "DEVICE", title: translate.gettext("DEVICE")},
            ];
            return this.renderDataTable(inventoryCategoryData, tableDefinition);
        }

        if (category === "SYSTEM" && mainData) {
            // Convert timestamps in the format like 738771:47060123 to a date, where the left part are days since year 0.0.0000 and the right part are milliseconds since midnight
            const convertTimestampToDate = (timestamp) => {
                const days = timestamp.split(":")[0];
                const milliseconds = timestamp.split(":")[1];

                const dateConverted = new Date(
                    ((days - 1 + 1721426) - 2440588) * 86400000 + (milliseconds * 1)
                );

                return dateConverted.toLocaleString();
            }

            return <div>
                <div><span className='emphasized'>{translate.gettext("Logonuser")}:</span> {mainData.LOGONUSER}, <span className='emphasized'>{translate.gettext("Scandate")}</span>: {convertTimestampToDate(mainData.SCANDATE)}</div>
                <div><span className='emphasized'>{translate.gettext("Last boot")}:</span> {mainData.LASTBOOT}, <span className='emphasized'>{translate.gettext("Last shutdown")}:</span> {mainData.LASTSHUTDOWN}, {translate.gettext("Uptime")}: {mainData.SYSTEMUPTIME}</div>
            </div>;
        }

        if (category === "OS" && mainData) {
            const optServicepack = (mainData.SERVICEPACK) ? <span>, <span className='emphasized'>{translate.gettext("Servicepack")}</span>: {mainData.SERVICEPACK}</span> : <span></span>;
            return <div>
                <div><span className='emphasized'>{translate.gettext("Edition")}:</span> {mainData.OSNAME}{optServicepack}, <span className='emphasized'>{translate.gettext("Build")}:</span> {mainData.BUILD}, <span className='emphasized'>{translate.gettext("Installdate")}:</span> {mainData.INSTALLDATE}</div>
            </div>;
        }

        if (category === "OS_HOTFIX" && mainData) {
            // Sort by install date (newest first)
            inventoryCategoryData.sort((a, b) => {
                const aDate = a.INSTALLDATE || "0000-00-00";
                const bDate = b.INSTALLDATE || "0000-00-00";

                const aDateParsed = Date.parse(aDate);
                const bDateParsed = Date.parse(bDate);

                return bDateParsed - aDateParsed;
            });

            const tableDefinition = [
                {key: "HOTFIX_ID", title: translate.gettext("HOTFIX_ID")},
                {key: "DESCRIPTION", title: translate.gettext("DESCRIPTION")},
                {key: "INSTALLDATE", title: translate.gettext("INSTALLDATE")}
            ];
            return this.renderDataTable(inventoryCategoryData, tableDefinition);
        }

        if (category === "LOCALINFO" && mainData) {
            return <div>
                <div><span className='emphasized'>{translate.gettext("Language")}:</span> {mainData.FULLLOCALIZELANGUAGENAME}, <span className='emphasized'>{translate.gettext("Build")}:</span> {mainData.BUILD}</div>
            </div>;
        }

        if (category === "PROGRAMS" && mainData) {
            // Sort by install date (newest first)
            inventoryCategoryData.sort((a, b) => {
                const aDate = a.INSTALLDATE || "0000-00-00";
                const bDate = b.INSTALLDATE || "0000-00-00";

                const aDateParsed = Date.parse(aDate);
                const bDateParsed = Date.parse(bDate);

                return bDateParsed - aDateParsed;
            });

            const tableDefinition = [
                {key: "COMPANY", title: translate.gettext("COMPANY")},
                {key: "PRODUKT", title: translate.gettext("PRODUKT")},
                {key: "SWVERSION", title: translate.gettext("SWVERSION")},
                {key: "INSTALLDATE", title: translate.gettext("INSTALLDATE")}
            ];
            return this.renderDataTable(inventoryCategoryData, tableDefinition);
        }

        if (category === "DISPLAY" && mainData) {
            return inventoryCategoryData.map((displayData, i) => <div>
                <div key={`${baseKey}-DISPLAY-${i}`}>{displayData.ADAPTER}, <span className='emphasized'>{translate.gettext("Resolution")}:</span> {displayData.HORIZONTALRESOLUTION} x {displayData.VERTICALRESOLUTION}  - {displayData.COLORDEPTH} bit @ {displayData.VERTICALREFRESHRATE} Hz</div>
            </div>);
        }

        if (category === "DISK" && mainData) {
            return inventoryCategoryData.map((displayData, i) => {
                const chartData = [
                    { x: translate.gettext('Used'), y: Number(displayData.CAPACITY-displayData.FREESPACE) },
                    { x: translate.gettext('Free'), y: Number(displayData.FREESPACE) },
                ];

                return <div key={`${baseKey}-DISK-${i}`}>
                    <div><span className='emphasized'>{translate.gettext("Disk")}:</span> {displayData.DISK}: {displayData.VOLUMELABEL} ({displayData.FILESYSTEM}), <span className='emphasized'>{translate.gettext("Capacity")}:</span> {displayData.CAPACITY} MB ({displayData.FREESPACE} MB {translate.gettext("free")})</div>

                    {renderChart(chartData)}
                </div>
            });
        }

        if (category === "NEVI" && inventoryCategoryData && inventoryCategoryData.length) {
            // console.log("::: NEVI", inventoryCategoryData);
            const staticCols = ["HASH", "IP", "MAC", "HOST", "OS", "OS_DESCRIPTION", "ObjectID", "RTIME", "VENDOR", "NAME", "DESCRIPTION"];
            const dynamicCols = Object.keys(inventoryCategoryData[0]).filter(key => staticCols.indexOf(key) === -1);

            const tableDefinition = [
                {key: "IP_MAC", title: translate.gettext("IP / MAC"), cellDataFn: (colData) => <span>{colData.IP}<br/>{colData.MAC}</span>},
                {key: "HOST", title: translate.gettext("HOST")},
                {key: "VENDOR_OS", title: translate.gettext("VENDOR / OS"), cellDataFn: (colData) => <span>{colData.VENDOR}<br/>{colData.OS}</span>},
                ...dynamicCols.map(key => ({key, title: translate.gettext(key)})),
            ];

            return this.renderDataTable(inventoryCategoryData, tableDefinition);
        }

        return <div></div>;
    }

    render() {
        const { inventoryItem, settings } = this.props;

        const blocksToRender = [
            "SYSTEM",
            "BIOS",
            "CPU",
            "MEMORYDEVICE",
            "DISK",
            "DISPLAY",
            "OS",
            "OS_HOTFIX",
            "LOCALINFO",
            "PROGRAMS",
            "NEVI",
        ];

        const result = blocksToRender.map(category => {
            const inventoryCategoryData = inventoryItem[category];
            // console.log("::: renderBlocks", category, inventoryCategoryData);

            const randomKey = Math.random().toString(36).substring(7);
            const categoryEnabled = settings[`displayCategory${category}`];
            if (!categoryEnabled || !inventoryCategoryData || !inventoryCategoryData.length) {
                return <div key={`row-${category}-${randomKey}`}></div>;
            }

            return <div key={`row-${category}-${randomKey}`}>
                <h4 className='subheadline'>{translate.gettext(category)}</h4>
                {this.renderBlocks(category, `row-${category}-${randomKey}`, inventoryCategoryData)}
            </div>
        });

        return <div>{result}</div>;
    }
}


// WEBPACK FOOTER //
// ./src/components/widget/inventory/listInventory/InventoryTable.tsx